import { FeatureList } from "@/components/atoms/FeatureList";
import { ServiceUnavailableDashIcon } from "@/components/atoms/icons/ServiceUnavailableDashIcon";
import { SelectPlanContext } from "@/components/atoms/SelectPlan";
import { Divider } from "@/components/atoms/utilities/Divider";
import { Service } from "@/generated/graphql_billing";
import { color } from "@/styles/theme";
import { Icon } from "@moneyforward/mfx_biz_react_components";
import { useContext } from "react";

/**
 * `Props` is a type representing the properties for the `PlanServiceCell` component.
 * @typedef {Object} Props - The properties to pass to the `PlanServiceCell` component.
 * @property {Service} service - The service for which the plan features are to be displayed.
 */
type Props = {
  service?: Service;
};

// `PlanServiceCell` is a functional component that renders a cell for a service plan.
export const PlanServiceCell = ({ service }: Props) => {
  const ctx = useContext(SelectPlanContext);

  // service is not available
  // -> display a dashed line
  if (!service || !service.allFeatures) {
    return <ServiceUnavailableDashIcon />;
  }

  // service available +
  // - service has no features that should be shown; or
  // - service has no features;
  // -> display a check icon
  if (
    Object.keys(ctx.showFeaturesFor[service.code]).length == 0 ||
    service.allFeatures.length === 0
  ) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CheckIcon />
      </div>
    );
  }

  // service has one or more features (which may or may not be enabled)
  // -> display a check icon and the list of features
  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "start",
        alignItems: "center",
        padding: "8px 16px",
        gap: 2,
        maxWidth: 240,
      }}
    >
      <CheckIcon />
      <Divider width={"85%"} height={1} />
      <FeatureList service={service} />
    </div>
  );
};

const CheckIcon = () => {
  return (
    <div css={{ height: 24, width: 24 }}>
      <Icon color={color.primary} name="check" />
    </div>
  );
};
